import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { toast } from "components/ui/toast";
import { Mail, Building2, Info, ShieldCheck } from "lucide-react";
import { useAddIdentity } from "core/features/identity/hooks";
import {
    Tooltip,
    TooltipProvider,
    TooltipTrigger,
    TooltipContent,
} from "components/ui/tooltip";

interface AddIdentityProps {
    open: boolean;
    onClose: () => void;
}

export default function AddIdentity({ open, onClose }: AddIdentityProps) {
    const [email, setEmail] = useState("");
    const [friendlyName, setFriendlyName] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);

    const addMut = useAddIdentity();

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const handleVerify = async () => {
        setIsSubmitted(true);
        if (!EMAIL_REGEX.test(email.trim()) || !friendlyName.trim()) return;

        try {
            toast.info("Sending verification email...");
            await addMut.mutateAsync({ email: email.trim(), friendlyName: friendlyName.trim() });
            toast.dismiss();
            toast.success(
                "Success! A verification email has been sent. Please check your inbox (and spam)."
            );
            setEmail("");
            setFriendlyName("");
            setIsSubmitted(false);
            onClose();
        } catch (error) {
            console.error("Error verifying email:", error);
            toast.dismiss();
            toast.error("Failed to verify email. Please try again.");
        }
    };

    return (
        // `onOpenChange` was missing, so Radix had no way to report a dismissal:
        // Escape and clicking the overlay did nothing, and the only exit was the
        // Cancel button. The handler already existed — it just was not wired up.
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent size="md" align="center" padding={false} showClose={false} className="overflow-hidden" aria-labelledby="add-identity-title">

                {/* ── Gradient Header ── */}
                <div className="relative bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-700 px-6 py-5">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                            <ShieldCheck className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 id="add-identity-title" className="text-base font-bold text-white leading-tight">Verify a New Email Address</h2>
                            <p className="text-xs text-white/65 mt-0.5">A verification link will be sent to your inbox</p>
                        </div>
                    </div>

                    {/* Info banner */}
                    <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 text-xs text-white/80 leading-relaxed">
                        Enter your email and business name below and click{" "}
                        <span className="font-semibold text-white">Verify</span>.{" "}
                        Please also check your <span className="font-semibold text-white">spam folder</span> if you don't see it within a few minutes.
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="px-6 py-5 space-y-5">

                    {/* Email Field */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400">1</span>
                            </div>
                            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                Email Address
                            </label>
                        </div>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle pointer-events-none" />
                            <Input
                                type="email"
                                className={`pl-9 h-10 text-sm ${isSubmitted && !EMAIL_REGEX.test(email) ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                                placeholder="example@yourdomain.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        {isSubmitted && !EMAIL_REGEX.test(email) && (
                            <p className="text-red-500 dark:text-red-400 text-xs flex items-center gap-1 mt-1">
                                <span className="w-3.5 h-3.5 rounded-full bg-red-500 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">!</span>
                                Enter a valid email address
                            </p>
                        )}
                    </div>

                    {/* Business Name Field */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400">2</span>
                            </div>
                            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                Business Name
                                <TooltipProvider delayDuration={150}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                aria-label="Business name help"
                                                className="inline-flex items-center justify-center rounded p-0.5 hover:bg-gray-100 dark:hover:bg-dark-700 focus-ring normal-case"
                                            >
                                                <Info className="w-3.5 h-3.5 text-content-subtle hover:text-content-secondary dark:hover:text-gray-300" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" arrow>
                                            Use a business name that reflects your brand identity
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </label>
                        </div>
                        <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle pointer-events-none" />
                            <Input
                                type="text"
                                className={`pl-9 h-10 text-sm ${isSubmitted && !friendlyName ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                                placeholder="Your Business Name"
                                value={friendlyName}
                                onChange={(e) => setFriendlyName(e.target.value)}
                            />
                        </div>
                        {isSubmitted && !friendlyName && (
                            <p className="text-red-500 dark:text-red-400 text-xs flex items-center gap-1 mt-1">
                                <span className="w-3.5 h-3.5 rounded-full bg-red-500 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">!</span>
                                Enter a valid business name
                            </p>
                        )}
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="px-6 py-4 border-t border-line-subtle dark:border-white/[0.06] flex items-center justify-end gap-3 bg-gray-50/50 dark:bg-white/[0.02]">
                    <Button
                        variant="secondary"
                        className="rounded-xl text-sm px-5"
                        onClick={onClose}
                        disabled={addMut.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleVerify}
                        disabled={addMut.isPending}
                        className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold shadow-md shadow-indigo-500/25 gap-2 text-sm px-5"
                    >
                        <ShieldCheck className="w-4 h-4" />
                        {addMut.isPending ? "Sending…" : "Verify Email"}
                    </Button>
                </div>

            </DialogContent>
        </Dialog>
    );
}
